/**
 * ─── Jewelry Shop Dashboard + Inventory + POS ────────────────
 * DDD: JewelryItem (AR), SalesOrder (AR), RateCard (AR).
 * PriceCalculator domain service for all price computations.
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import {
  jewelryItemRepo, rateCardRepo, salesOrderRepo,
  repairJobRepo, consignmentRepo, jewelryCustomerRepo,
} from '../../lib/db'
import {
  PriceCalculator,
  ITEM_CATEGORY_LABELS, METAL_TYPE_LABELS, ITEM_STATUS_LABELS,
  ORDER_STATUS_LABELS, PAYMENT_METHOD_LABELS,
  JOB_STATUS_LABELS, JOB_TYPE_LABELS, JOB_STATE_TRANSITIONS,
  CONSIGNMENT_STATUS_LABELS,
} from '@repo/entity-jewelry'
import type { JewelryItem, RateCard } from '@repo/entity-jewelry'
import {
  Gem, TrendingUp, ShoppingBag, Wrench, Handshake, Users,
  ArrowRight, Plus, Calculator, DollarSign,
} from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════════════

export function JewelryDashboardPage() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<any>({})
  const [items, setItems] = useState<any[]>([])
  const [orders, setOrders] = useState<any[]>([])
  const [latestRate, setLatestRate] = useState<any>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [i, o, r, c, rep, con] = await Promise.all([
        jewelryItemRepo.findMany({ page: 1, pageSize: 500 }),
        salesOrderRepo.findMany({ page: 1, pageSize: 100, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        rateCardRepo.findMany({ page: 1, pageSize: 10, sort: [{ field: 'effectiveAt', direction: 'desc' }] }),
        jewelryCustomerRepo.findMany({ page: 1, pageSize: 500 }),
        repairJobRepo.findMany({ page: 1, pageSize: 100 }),
        consignmentRepo.findMany({ page: 1, pageSize: 100 }),
      ])
      const itemList: any[] = 'items' in i ? i.items : []
      const orderList: any[] = 'items' in o ? o.items : []
      const rateList: any[] = 'items' in r ? r.items : []
      const custList: any[] = 'items' in c ? c.items : []
      const repairList: any[] = 'items' in rep ? rep.items : []
      const consignList: any[] = 'items' in con ? con.items : []

      setItems(itemList)
      setOrders(orderList)
      setLatestRate(rateList[0] ?? null)

      const totalInventoryValue = itemList
        .filter((x: any) => x.status === 'in_stock')
        .reduce((s: number, x: any) => s + (x.costBasis ?? 0), 0)
      const totalSales = orderList
        .filter((x: any) => x.status === 'completed')
        .reduce((s: number, x: any) => s + (x.grandTotal ?? 0), 0)
      const inStock = itemList.filter((x: any) => x.status === 'in_stock').length

      setStats({
        inStock, totalInventoryValue, totalSales,
        totalCustomers: custList.length,
        activeRepairs: repairList.filter((x: any) => x.status !== 'delivered' && x.status !== 'cancelled').length,
        activeConsignments: consignList.filter((x: any) => x.status === 'active').length,
        goldRate: latestRate?.ratePerGram ?? 0,
      })
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Jewelry Shop</h2>
        <p className="mt-1 text-sm text-gray-500">
          {latestRate ? `Today's Gold Rate: ₱${(latestRate.ratePerGram ?? 0).toLocaleString()}/gram (${latestRate.metalType})` : 'No rate set'}
        </p>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['In Stock', stats.inStock ?? 0, 'purple', Gem],
          ['Inventory Value', `₱${(stats.totalInventoryValue ?? 0).toLocaleString()}`, 'blue', DollarSign],
          ['Total Sales', `₱${(stats.totalSales ?? 0).toLocaleString()}`, 'green', ShoppingBag],
          ['Gold Rate', `₱${(stats.goldRate ?? 0).toLocaleString()}/g`, 'yellow', TrendingUp],
          ['Customers', stats.totalCustomers ?? 0, 'indigo', Users],
          ['Active Repairs', stats.activeRepairs ?? 0, 'blue', Wrench],
          ['Consignments', stats.activeConsignments ?? 0, 'red', Handshake],
        ].map(([label, value, color, Icon]: any) => (
          <div key={label} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <div className="flex items-center gap-2">
              {Icon && <Icon className={`h-4 w-4 text-${color}-600`} />}
              <p className={`text-xs text-${color}-600`}>{label}</p>
            </div>
            <p className={`mt-1 text-xl font-bold text-${color}-700`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Orders */}
        <Card>
          <CardHeader title="Recent Sales" description={`${orders.length} orders`}
            action={<Link to="/jewelry/pos" className="text-xs text-blue-600 hover:underline flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></Link>} />
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {orders.slice(0, 8).map((o: any) => (
              <div key={o.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{o.orderNumber ?? o.id?.slice(0, 8)}</p>
                  <p className="text-xs text-gray-500">{o.customerName ?? 'Walk-in'} · {o.paymentMethod}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-700">₱{(o.grandTotal ?? 0).toLocaleString()}</p>
                  <Badge color={o.status === 'completed' ? 'green' : 'yellow'}>
                    {ORDER_STATUS_LABELS[o.status as keyof typeof ORDER_STATUS_LABELS] ?? o.status}
                  </Badge>
                </div>
              </div>
            ))}
            {orders.length === 0 && <p className="p-4 text-sm text-gray-400">No sales yet.</p>}
          </div>
        </Card>

        {/* Inventory Snapshot */}
        <Card>
          <CardHeader title="Inventory" description={`${items.length} items`}
            action={<Link to="/jewelry/items" className="text-xs text-blue-600 hover:underline flex items-center gap-1">View All <ArrowRight className="h-3 w-3" /></Link>} />
          <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
            {items.filter((x: any) => x.status === 'in_stock').slice(0, 8).map((item: any) => (
              <div key={item.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.description}</p>
                  <p className="text-xs text-gray-500">{item.tagNumber} · {item.weightGrams}g · {item.purityKarat}K {item.metalType}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">₱{(item.costBasis ?? 0).toLocaleString()}</p>
                  <Badge color="purple">{ITEM_CATEGORY_LABELS[item.category as keyof typeof ITEM_CATEGORY_LABELS] ?? item.category}</Badge>
                </div>
              </div>
            ))}
            {items.filter((x: any) => x.status === 'in_stock').length === 0 && <p className="p-4 text-sm text-gray-400">No items in stock.</p>}
          </div>
        </Card>
      </div>
    </div>
  )
}

import { JewelryInventoryPage } from './jewelry-inventory'
import { JewelryPOSPage } from './jewelry-inventory'
import { JewelryRatePage, JewelryRepairPage, JewelryCustomerPage } from './jewelry-pages'
